import {
  Box,
  Stack,
  Typography,
  useTheme,
  Toolbar,
  Container,
  Collapse,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  useCurrentStore,
  useOrdering,
  useRecaptcha,
  useSectionsScrolling,
} from 'hooks'
import { useNavigate } from 'react-router-dom'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { createSubmitParamsFromOrder, updateOrderForSubmitting } from 'services'
import { SubmitOrderParams } from './SubmitOrderParams'
import { OrderFootnotes } from './OrderFootnotes'
import { OrderTips } from './OrderTips'
import { OrderPaymentInfo } from './OrderPaymentInfo'
import { SubmitOrderSection } from './SubmitOrderSection'
import { OrderInfo } from './OrderInfo'
import { OrderCustomerInfo } from './OrderCustomerInfo'
import { OrderReview } from './OrderReview'
import { SubmitOrderHeader } from './SubmitOrderHeader'
import { KTab, KTabs } from 'components/core/KTabs'
import { KBottomAppBar } from 'components/core/KBottomAppBar'
import { BottomBarActionButton } from './BottomBarActionButton'

interface Section {
  label: string
  name: string
  element: () => ReactNode
}

interface SubmitOrderFormState {
  sections: Section[]
  currentSectionIndex: number
}

interface SubmitOrderFormProps {
  acceptOrderInfo: boolean
  acceptFootnotes: boolean
  acceptTip: boolean
  acceptPayment: boolean
  acceptCustomerInfo: boolean
  acceptPromoCode: boolean
  backUrl: string
}

export const SubmitOrderForm = ({
  acceptOrderInfo,
  acceptFootnotes,
  acceptPayment,
  acceptTip,
  acceptCustomerInfo,
  acceptPromoCode,
  backUrl,
}: SubmitOrderFormProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()

  const { formatCurrency, store } = useCurrentStore()

  const [state, setState] = useState<SubmitOrderFormState>({
    sections: [],
    currentSectionIndex: 0,
  })

  const { order, saveOrder, submitOrder } = useOrdering()
  const form = useForm<SubmitOrderParams>({
    defaultValues: createSubmitParamsFromOrder(order),
    mode: 'onChange',
  })

  useEffect(() => {
    if (!store) return

    const optionalSections: Section[] = []
    if (acceptOrderInfo) {
      optionalSections.push({
        label: t('Order info'),
        name: 'orderInfo',
        element: () => (
          <SubmitOrderSection label={t('Order info')}>
            <OrderInfo submitForm={{ form }} />
          </SubmitOrderSection>
        ),
      })
    }
    if (acceptCustomerInfo) {
      optionalSections.push({
        label: t('Customer info'),
        name: 'customerInfo',
        element: () => <OrderCustomerInfo form={form} />,
      })
    }
    if (acceptFootnotes) {
      optionalSections.push({
        label: t('Footnotes'),
        name: 'footnotes',
        element: () => <OrderFootnotes />,
      })
    }
    if (acceptTip) {
      optionalSections.push({
        label: t('Tips'),
        name: 'tips',
        element: () => <OrderTips />,
      })
    }
    if (acceptPayment) {
      optionalSections.push({
        label: t('Payment info'),
        name: 'paymentInfo',
        element: () => <OrderPaymentInfo form={form} />,
      })
    }

    setState((s) => ({
      ...(s as any),
      sections: [
        ...optionalSections,
        {
          label: t('Order summary'),
          name: 'summary',
          element: () => <OrderReview acceptPromoCode={acceptPromoCode} />,
        },
      ],
    }))
  }, [store, form, t]) // eslint-disable-line react-hooks/exhaustive-deps

  const collapseRef = useRef<HTMLDivElement | null>(null)
  const {
    scrollToSection,
    scrollRef,
    sectionsRef,
    shouldShowHeader,
    currentSectionIndex,
  } = useSectionsScrolling()

  const handleSectionChange = (sectionIndex: number) => {
    scrollToSection(sectionIndex)
    setState({
      ...state,
      currentSectionIndex: sectionIndex,
    })
  }

  useEffect(() => {
    if (currentSectionIndex !== state.currentSectionIndex) {
      setState((state) => ({
        ...state,
        currentSectionIndex,
      }))
    }
  }, [currentSectionIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const { getSubmitToken } = useRecaptcha()

  const _submitOrder = useCallback(async () => {
    if (!order || !store) return
    // Keep the Redux `order` state (and thus the payment form UI) holding the
    // user-typed, formatted card values. `updateOrderForSubmitting` strips
    // formatting for the API payload only — it must not be persisted back to
    // Redux, otherwise the Valid Thru/Card number fields lose their format
    // (e.g. "12/34" -> "1234") after a failed submit.
    const savedOrder = saveOrder(order)
    const orderPayload = updateOrderForSubmitting(savedOrder, store)
    const submitToken = await getSubmitToken()
    await submitOrder(orderPayload, submitToken)
  }, [submitOrder, saveOrder, order, store, getSubmitToken])

  if (!store) return null

  const buttonBarActionSxProp = {
    [theme.breakpoints.down('md')]: {
      minWidth: 210,
    },
  }

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Container maxWidth="lg">
          <Collapse in={shouldShowHeader} ref={collapseRef}>
            <SubmitOrderHeader />
          </Collapse>
          <Stack direction="row" spacing={2}>
            <KTabs
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, newValue) => handleSectionChange(newValue)}
              value={state.currentSectionIndex}
              sx={{ flexGrow: 1 }}
            >
              {state.sections.map((section, index) => {
                const sectionError =
                  form.formState.errors[section.name as keyof SubmitOrderParams]
                // if (sectionError) {
                //   console.debug('sectionError', section.label, sectionError)
                // }
                return (
                  <KTab
                    key={index}
                    label={section.label}
                    value={index}
                    sx={
                      sectionError && {
                        color: theme.palette.error.main,
                        '&.Mui-selected': { color: theme.palette.error.main },
                      }
                    }
                  />
                )
              })}
            </KTabs>
          </Stack>
        </Container>
        <Box
          component="form"
          noValidate
          autoComplete="one-time-code"
          ref={scrollRef}
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Container maxWidth="lg">
            {state.sections.map(({ label, element }, index) => (
              <Box
                key={label}
                ref={(el) => (sectionsRef.current[index] = el as any)}
                sx={{ py: 2 }}
              >
                {element()}
              </Box>
            ))}
          </Container>
        </Box>
      </Stack>

      <KBottomAppBar>
        <Toolbar
          disableGutters
          sx={{
            [theme.breakpoints.down('sm')]: {
              gap: theme.spacing(1),
              justifyContent: 'space-between',
            },
          }}
        >
          <Stack
            sx={{
              marginY: 0.5,
              flexGrow: 1,
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
            }}
          >
            <Typography color="secondary" variant="body2">
              {t('Total')}
            </Typography>
            <Typography variant="h3" color="text.primary">
              {formatCurrency(order?.total ?? 0)}
            </Typography>
          </Stack>
          <BottomBarActionButton
            sx={{
              ...buttonBarActionSxProp,
              [theme.breakpoints.down('sm')]: {
                width: '40%',
                minWidth: 100,
              },
            }}
            variant="secondary"
            onClick={() => navigate(backUrl)}
          >
            {t('Back to menu')}
          </BottomBarActionButton>
          <BottomBarActionButton
            sx={{
              ...buttonBarActionSxProp,
              [theme.breakpoints.down('sm')]: {
                width: '50%',
                minWidth: 100,
              },
            }}
            disabled={order?.total === 0}
            onClick={(e) => {
              form.handleSubmit(_submitOrder)(e)

              // react-hook-form does not scroll to the error without form control
              // so we have to do it manually here
              if (form.formState.errors.orderInfo?.deliveryFee) {
                handleSectionChange(0)
              }
            }}
          >
            {t('Place your order')}
          </BottomBarActionButton>
        </Toolbar>
      </KBottomAppBar>
    </Stack>
  )
}
